import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { Observation as FhirObservation } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import type * as fhirTypes from "fhir/r4";
import { NORMAL_EXAM_FINDINGS } from "../components/Objective/normalExam";

interface PredefinedEntry {
  observationCode: fhirTypes.CodeableConcept;
  observationResource: fhirTypes.Observation | null;
}

export interface FillNormalExamResult {
  matched: number; // rows we had a normal finding for and tried to save
  failed: string[]; // labels of rows whose save failed
}

export const useFillNormalExam = ({
  patientId,
  encounterId,
}: {
  patientId?: string;
  encounterId?: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<FillNormalExamResult, PredefinedEntry[]>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Observation.FILL_NORMAL_EXAM({
        patientId,
        encounterId,
      }),
      mutationFn: async (predefined) => {
        // Keep only the rows we have a normal finding for.
        const targets = predefined.filter(
          (entry) =>
            entry.observationCode.text != null &&
            NORMAL_EXAM_FINDINGS[entry.observationCode.text] != null,
        );

        const settled = await Promise.allSettled(
          targets.map((entry) => {
            const valueString =
              NORMAL_EXAM_FINDINGS[entry.observationCode.text as string];
            const observationId = entry.observationResource?.id;

            // Overwrite an existing observation (PATCH), else create one (POST).
            if (observationId) {
              return axiosInstance.patch(
                `/charting/observation-generic/${observationId}`,
                { fhirValue: { valueString } },
              );
            }
            return axiosInstance.post("/charting/observation-generic/", {
              patientId,
              encounterId,
              category: FhirObservation.Category.EXAM,
              method: FhirObservation.Method.values.MANUAL_FREE_TEXT,
              code: entry.observationCode,
              status: FhirObservation.Status.FINAL,
              fhirValue: { valueString },
            });
          }),
        );

        const failed = targets
          .filter((_, i) => settled[i].status === "rejected")
          .map((entry) => entry.observationCode.text as string);

        return { matched: targets.length, failed };
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Observation.DEFAULT({
            patientId,
            encounterId,
          }),
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTER({
            patientId,
            motherEncounterId: encounterId,
          }),
        });
      },
    },
  });
};
